import React, { useState, useRef, useEffect } from "react";
import { InputText } from "primereact/inputtext";
import styles from "./styles.module.css";

const MAX_LENGTH = 15;

type Props = {
  label?: string;
  value?: string;
  name?: string;
  onChange?: (e: { target: { value: string; name?: string } }) => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
};

export function NewUcInput({
  label,
  value = "",
  onChange,
  name,
  placeholder,
  disabled,
  className,
  ...props
}: Props) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [displayValue, setDisplayValue] = useState("");

const formatValue = (rawValue: string): string => {
    // Remover qualquer caractere não numérico
    const digitsOnly = rawValue.replace(/\D/g, "");

    // Limitar a entrada ao comprimento máximo
    if (digitsOnly.length > MAX_LENGTH) {
        return ""; // Ou você pode retornar um valor formatado parcial, se preferir.
    }

    // Se não houver dígitos, retorna uma string vazia
    if (digitsOnly.length === 0) {
        return "";
    }

    // Variável para armazenar o resultado formatado
    let result = "";

    // Adiciona os grupos de 3 dígitos
    let remaining = digitsOnly;
    const length = remaining.length;

    // Para os primeiros 9 dígitos (do começo até o 9º)
    for (let i = length; i > 0; i -= 3) {
        const start = Math.max(0, i - 3);
        const group = remaining.slice(start, i);
        
        // Adiciona o grupo ao resultado
        result = group + (result ? `.${result}` : "");
        remaining = remaining.slice(0, start);
    }

    // Remove o primeiro ponto se existir
    result = result.replace(/^\./, "");

    // Adiciona o hífen e os últimos 2 dígitos se houver mais de 9 dígitos
    if (length > 9) {
        const lastTwoDigits = digitsOnly.slice(-2);
        const mainPart = digitsOnly.slice(0, -2);
        result = formatMainPart(mainPart) + `-${lastTwoDigits}`;
    }

    return result;
};

// Função auxiliar para formatar a parte principal para até 15 dígitos
const formatMainPart = (mainPart: string): string => {
    let result = "";
    const length = mainPart.length;

    // Adiciona os grupos de 3 dígitos
    for (let i = length; i > 0; i -= 3) {
        const start = Math.max(0, i - 3);
        const group = mainPart.slice(start, i);
        
        result = group + (result ? `.${result}` : "");
    }

    return result.replace(/^\./, ""); // Remove o ponto inicial, se existir
};

  // Atualiza o valor formatado quando o valor de entrada muda
  useEffect(() => {
    if (value !== undefined) {
      setDisplayValue(formatValue(value));
    }
  }, [value]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value;
    // Remove caracteres não numéricos para processamento
    const digitsOnly = inputValue.replace(/\D/g, "");

    if (digitsOnly.length > MAX_LENGTH) {
      return;
    }

    // Formatar o valor para exibição
    const formattedValue = formatValue(digitsOnly);
    setDisplayValue(formattedValue);

    // Chamar o onChange com apenas os dígitos
    if (onChange) {
      onChange({
        target: {
          value: digitsOnly,
          name,
        },
      });
    }
  };

  return (
    <div className={styles.container}>
      {label && <label className={styles.label}>{label}</label>}
      <InputText
        ref={inputRef}
        value={displayValue}
        onChange={handleChange}
        placeholder={placeholder}
        disabled={disabled}
        className={className}
        name={name} // Adicionar o name ao InputText
        {...props}
      />
    </div>
  );
}
